import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { StoryUploadService } from './story-upload.service';
import { CreateUploadSessionResponse, UploadItem } from './story-upload.api';
import {StoryVideoDraft, VideoStoryComposerDialogComponent} from "./video-story-composer/video-story-composer.dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {SharedModule} from "../../../shared/shared.module";
import {AdvFormService} from "../services/adv-form.service";

export type UploadedVideoAttachment = {
  session_id: string;
  asset_id?: string;
  final_key?: string;
  temp_key?: string;

  // Optional metadata you can attach later to another object
  start_ms?: number;
  end_ms?: number;
  muted?: boolean;
  caption?: string;

  // for UI preview
  preview_url?: string; // local object url or public_url if exists
};

@Component({
    selector: 'app-story-video-uploader',
    templateUrl: './story-video-uploader.component.html',
    styleUrls: ['./story-video-uploader.component.scss'],
    imports: [
        MatIconModule,
        MatButtonModule,
        SharedModule
    ]
})
export class StoryVideoUploaderComponent implements OnInit, OnDestroy {
  @Output() uploaded = new EventEmitter<UploadedVideoAttachment | null>();

  uploading = false;
  progress = 0;

  sessionId?: string;
  uploadSub?: Subscription;

  // UI after upload
  uploadedMeta?: UploadedVideoAttachment;
  previewUrl?: string;

  constructor(private dialog: MatDialog, private storyUpload: StoryUploadService, private formService: AdvFormService) {}

  /** Back on this step: show the video already uploaded in this session. */
  ngOnInit(): void {
    const saved = this.formService.uploadedVideo;
    if (saved?.meta?.session_id && saved.meta.session_id === this.formService.getQuestion('session_id')) {
      this.uploadedMeta = saved.meta;
      this.previewUrl = saved.previewUrl;
      this.sessionId = saved.meta.session_id;
      this.progress = 100;
    }
  }

  ngOnDestroy(): void {
    this.uploadSub?.unsubscribe();
    // Keep the preview alive while the upload belongs to the form (the user
    // may come back to this step); revoke only a preview that was dropped.
    if (!this.formService.uploadedVideo) this.cleanupPreview();
  }

  async pickVideo(input: HTMLInputElement) {
    const file = input.files?.[0] || null;
    input.value = '';
    if (!file) return;

    // 1) Open composer dialog (caption + trim range). Optional; remove if not needed.
    const dialogRef = this.dialog.open(VideoStoryComposerDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: { file, maxDurationSec: 30 },
      panelClass: 'story-dialog',
    });

    const draft = (await dialogRef.afterClosed().toPromise()) as StoryVideoDraft | null;
    if (!draft) return;

    const contentType = file.type || 'video/mp4';
    const ext = this.extFromFileName(file.name) || this.extFromContentType(contentType) || '.mp4';

    // reset state
    this.uploading = true;
    this.progress = 0;
    this.uploadedMeta = undefined;
    this.cleanupPreview();

    // 2) Create upload session
    const sessionRes = (await this.storyUpload
        .createSession({ files: [{ kind: 'video', content_type: contentType, ext }] })
        .toPromise()) as CreateUploadSessionResponse;

    if (!sessionRes?.session_id) {
      this.resetUploadUI();
      throw new Error('Missing session_id in response');
    }

    this.sessionId = sessionRes.session_id;

    // ✅ Your backend returns uploads (Flutter). Fallback to files.
    const uploads = (sessionRes.uploads ?? sessionRes.files ?? []) as UploadItem[];
    if (!uploads.length) {
      this.resetUploadUI();
      throw new Error('Missing uploads/files in session response');
    }

    const u0 = uploads[0];
    const uploadUrl = u0.upload_url;
    if (!uploadUrl) {
      this.resetUploadUI();
      throw new Error('Missing upload_url in session response');
    }

    // 3) Upload to S3 with progress
    this.uploadSub?.unsubscribe();
    this.uploadSub = this.storyUpload.uploadToPresignedUrl(uploadUrl, file).subscribe({
      next: (p) => (this.progress = p),
      error: (err) => {
        console.error(err);
        this.resetUploadUI();
      },
      complete: async () => {
        // ✅ NO COMMIT STAGE
        this.uploading = false;
        this.progress = 100;

        // Preview (instant): local object URL
        this.previewUrl = await this.storyUpload.getPresignUrl(u0.final_key).toPromise();
        this.formService.addQuestion('session_id', sessionRes.session_id);
        // Save meta for attaching later to another object
        this.uploadedMeta = {
          session_id: sessionRes.session_id,
          asset_id: u0.asset_id,
          final_key: u0.final_key,
          temp_key: u0.temp_key,
          start_ms: draft.startMs,
          end_ms: draft.endMs,
          muted: draft.muted,
          caption: draft.caption,
          preview_url: this.previewUrl['url'] || u0.public_url ,
        };

        this.formService.uploadedVideo = {meta: this.uploadedMeta, previewUrl: this.previewUrl};

        // Emit to parent
        this.uploaded.emit(this.uploadedMeta);
      },
    });
  }

  async cancelUpload() {
    if (!this.sessionId) return;

    this.uploadSub?.unsubscribe();

    try {
      await this.storyUpload.cancelSession(this.sessionId).toPromise();
    } catch (e) {
      console.error(e);
    } finally {
      this.resetAll();
    }
  }

  removeVideo() {
    this.resetAll();
    // The removed video must not be published either.
    this.formService.uploadedVideo = null;
    if (this.formService.getQuestion('session_id')) this.formService.deleteQuestion('session_id');
    this.uploaded.emit(null);
  }

  private resetUploadUI() {
    this.uploading = false;
    this.progress = 0;
    this.sessionId = undefined;
  }

  private resetAll() {
    this.resetUploadUI();
    this.uploadedMeta = undefined;
    this.cleanupPreview();
  }

  private cleanupPreview() {
    if (this.previewUrl) {
      try {
        URL.revokeObjectURL(this.previewUrl);
      } catch {}
      this.previewUrl = undefined;
    }
  }

  private extFromFileName(name: string): string | null {
    const i = name.lastIndexOf('.');
    if (i < 0) return null;
    return name.substring(i).toLowerCase();
  }

  private extFromContentType(ct: string): string | null {
    if (!ct) return null;
    if (ct.includes('mp4')) return '.mp4';
    if (ct.includes('quicktime')) return '.mov';
    if (ct.includes('x-matroska')) return '.mkv';
    return '.mp4';
  }
}
